import { Node } from 'prosemirror-model';
import { Transaction } from 'prosemirror-state';
import { EditorView, NodeView } from 'prosemirror-view';
import { TASK_TOGGLE_META } from '../plugins/task-plugin';
import { lockedModePluginKey } from '../plugins/locked-mode-plugin';
import { convertTaskToParagraph, handleTaskEnter } from '../commands/task-commands';

export class TaskNodeView implements NodeView {
  dom: HTMLDivElement;
  contentDOM: HTMLSpanElement;

  private node: Node;
  private view: EditorView;
  private getPos: () => number | undefined;
  private checkbox: HTMLSpanElement;
  private toggle: HTMLSpanElement | null = null;

  constructor(node: Node, view: EditorView, getPos: () => number | undefined) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;

    // Create outer container
    this.dom = document.createElement('div');
    this.dom.className = 'task';
    if (node.attrs.done) this.dom.classList.add('done');
    if (node.attrs.active) this.dom.classList.add('active');
    if (node.attrs.id) this.dom.dataset.id = node.attrs.id;

    // Checkbox (non-editable, click to toggle done)
    this.checkbox = document.createElement('span');
    this.checkbox.className = `task-checkbox${node.attrs.done ? ' done' : ''}`;
    this.checkbox.contentEditable = 'false';
    this.checkbox.addEventListener('click', this.onCheckboxClick);
    this.dom.appendChild(this.checkbox);

    // Content area - ProseMirror manages content here
    this.contentDOM = document.createElement('span');
    this.contentDOM.className = 'task-content';
    this.contentDOM.addEventListener('beforeinput', this.onBeforeInput);
    this.contentDOM.addEventListener('click', this.onContentClick);
    this.dom.appendChild(this.contentDOM);

    // Active toggle (only shown when not done)
    if (!node.attrs.done) {
      this.createToggle();
    }
  }

  private createToggle() {
    this.toggle = document.createElement('span');
    this.toggle.className = 'task-active-toggle';
    if (this.node.attrs.active) {
      this.toggle.classList.add('active');
    }
    this.toggle.contentEditable = 'false';
    this.toggle.addEventListener('click', this.onToggleClick);
    this.dom.appendChild(this.toggle);
  }

  private removeToggle() {
    if (this.toggle) {
      this.toggle.removeEventListener('click', this.onToggleClick);
      this.toggle.remove();
      this.toggle = null;
    }
  }

  private onCheckboxClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = this.getPos();
    if (pos === undefined) return;

    const newDone = !this.node.attrs.done;
    const tr = this.view.state.tr
      .setNodeMarkup(pos, null, {
        ...this.node.attrs,
        done: newDone,
        active: newDone ? false : this.node.attrs.active,
      })
      .setMeta(TASK_TOGGLE_META, true);
    this.view.dispatch(tr);
  };

  private onToggleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = this.getPos();
    if (pos === undefined) return;

    const tr = this.view.state.tr
      .setNodeMarkup(pos, null, {
        ...this.node.attrs,
        active: !this.node.attrs.active,
      })
      .setMeta(TASK_TOGGLE_META, true);
    this.view.dispatch(tr);
  };

  private onContentClick = (e: MouseEvent) => {
    // In locked mode, clicking content toggles done
    const isLocked = lockedModePluginKey.getState(this.view.state);
    if (!isLocked) return;

    e.preventDefault();
    e.stopPropagation();
    const pos = this.getPos();
    if (pos === undefined) return;

    const newDone = !this.node.attrs.done;
    const tr = this.view.state.tr
      .setNodeMarkup(pos, null, {
        ...this.node.attrs,
        done: newDone,
        active: newDone ? false : this.node.attrs.active,
      })
      .setMeta(TASK_TOGGLE_META, true);
    this.view.dispatch(tr);
  };

  private onBeforeInput = (e: InputEvent) => {
    const { inputType } = e;
    const { state } = this.view;
    const dispatch = (tr: Transaction) => this.view.dispatch(tr);

    // Handle Enter (insertParagraph or insertLineBreak)
    if (inputType === 'insertParagraph' || inputType === 'insertLineBreak') {
      if (handleTaskEnter(state, dispatch)) {
        e.preventDefault();
      }
      return;
    }

    // Handle Backspace (deleteContentBackward)
    if (inputType === 'deleteContentBackward') {
      if (convertTaskToParagraph(state, dispatch)) {
        e.preventDefault();
      }
      // Otherwise, let ProseMirror handle it normally
    }
  };

  update(node: Node): boolean {
    if (node.type !== this.node.type) return false;

    this.node = node;

    // Update classes
    this.dom.classList.toggle('done', node.attrs.done);
    this.dom.classList.toggle('active', node.attrs.active);
    this.checkbox.classList.toggle('done', node.attrs.done);

    // Update data-id
    if (node.attrs.id) {
      this.dom.dataset.id = node.attrs.id;
    } else {
      delete this.dom.dataset.id;
    }

    // Handle toggle visibility based on done state
    if (node.attrs.done && this.toggle) {
      this.removeToggle();
    } else if (!node.attrs.done && !this.toggle) {
      this.createToggle();
    }

    return true;
  }

  // Don't let ProseMirror handle click events on checkbox/toggle
  stopEvent(event: Event): boolean {
    const target = event.target as HTMLElement;
    if (event.type === 'mousedown' || event.type === 'click') {
      return target === this.checkbox || target === this.toggle;
    }
    return false;
  }

  destroy() {
    this.checkbox.removeEventListener('click', this.onCheckboxClick);
    this.contentDOM.removeEventListener('beforeinput', this.onBeforeInput);
    this.contentDOM.removeEventListener('click', this.onContentClick);
    this.removeToggle();
  }
}
